import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { hora } from '../nucleo/formato';
import { Botao } from './base';
import { Modal } from './sobreposicao';
import { TextoCortado } from './texto-cortado';

export type VisaoCalendario = 'semana' | 'dia' | 'mes';

export interface EventoCalendario {
  id: string;
  inicio: string;
  fim: string;
  titulo: string;
  subtitulo?: string;
  cor?: 'acento' | 'azul' | 'ciano' | 'roxo' | 'laranja' | 'verde' | 'neutro';
  /** Só informação (bloqueio de agenda): sai como texto na grade, sem botão que não faz nada (r8 visual B7). */
  estatico?: boolean;
  /**
   * Ficha 2026-11-03: cor da pessoa dona da agenda, como valor CSS (#rrggbb). Com ela, o card ganha o
   * fundo tingido e a borda forte (`.evento-pessoa`, app.css); sem ela, segue a borda fina de `cor` (Ausências, Jornada).
   */
  corPessoa?: string;
  /**
   * Reunião livre de dia inteiro (03/11): sem hora, em cada dia que ela ocupa. Sem faixa própria: na Semana e no Dia entra na
   * primeira linha da grade. `inicio` e `fim` são a meia-noite local do primeiro dia e do dia seguinte ao último.
   */
  diaInteiro?: boolean;
}

const COR: Record<NonNullable<EventoCalendario['cor']>, string> = {
  acento: 'border-l-acento',
  azul: 'border-l-azul',
  ciano: 'border-l-ciano',
  roxo: 'border-l-roxo',
  laranja: 'border-l-laranja',
  verde: 'border-l-verde',
  neutro: 'border-l-tinta-3',
};

/** Classe e estilo do card: o da pessoa (fundo e texto vêm do `.evento-pessoa`) ou o de sempre. */
function visualDoEvento(e: EventoCalendario, comHover = true): { classe: string; style?: CSSProperties } {
  if (e.corPessoa) return { classe: 'evento-pessoa', style: { '--cor-pessoa': e.corPessoa } as CSSProperties };
  return { classe: `border-l-2 bg-sup-2 ${COR[e.cor ?? 'acento']}${comHover ? ' hover:bg-sup-3' : ''}` };
}

/**
 * Teto de itens montados (r10 robustez B1: com 6.000 eventos a grade do Mês tinha 24 mil nós e cada troca travava segundos).
 * Na célula do Mês e na célula de hora da Semana e do Dia (r11 robustez B2: a faixa de horas limita as linhas, não os eventos
 * de cada célula, e 6.000 eventos montavam 24 mil nós): os 3 primeiros na tela e, na impressão, até `IMPRESSAO_POR_DIA`, com
 * "+N" que abre a lista do dia (Mês) ou daquele horário (Semana e Dia). Na lista: `LISTA_POR_VEZ` por vez, com "Mostrar mais".
 */
const TELA_POR_DIA = 3;
const IMPRESSAO_POR_DIA = 8;
export const LISTA_POR_VEZ = 50;

const chaveDia = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const HORA_INICIO = 7;
const HORA_FIM = 21;

/**
 * Faixa de horas da Semana e do Dia (r8 visual M1): a padrão (7h às 21h) estendida para cobrir os eventos e bloqueios
 * visíveis; antes um evento às 06:00 ou às 21:30 sumia da grade. Recebe a hora de início de cada evento.
 */
export function faixaDeHoras(horas: number[]): { inicio: number; fim: number } {
  let inicio = HORA_INICIO;
  let fim = HORA_FIM;
  for (const h of horas) {
    if (!Number.isFinite(h)) continue;
    inicio = Math.min(inicio, Math.max(0, Math.floor(h)));
    fim = Math.max(fim, Math.min(24, Math.floor(h) + 1));
  }
  return { inicio, fim };
}

export const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const somarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mesmoDia = (a: Date, b: Date) => a.toDateString() === b.toDateString();
export function inicioDaSemana(d: Date) {
  const dia = inicioDoDia(d);
  return somarDias(dia, -((dia.getDay() + 6) % 7));
}

/** Intervalo visível para buscar eventos na API. */
export function intervaloDe(visao: VisaoCalendario, ref: Date): { de: Date; ate: Date } {
  if (visao === 'dia') return { de: inicioDoDia(ref), ate: somarDias(inicioDoDia(ref), 1) };
  if (visao === 'semana') return { de: inicioDaSemana(ref), ate: somarDias(inicioDaSemana(ref), 7) };
  const primeiro = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const de = inicioDaSemana(primeiro);
  return { de, ate: somarDias(de, 42) };
}

/** Título da semana sem travessão: duas datas formatadas à parte, sem repetir ano (nem mês, fora do inglês) quando iguais. */
function tituloSemana(loc: string, a: Date, b: Date, juntar: (de: string, ate: string) => string) {
  const completa = new Intl.DateTimeFormat(loc, { day: '2-digit', month: 'short', year: 'numeric' });
  if (a.getFullYear() !== b.getFullYear()) return juntar(completa.format(a), completa.format(b));
  const mesmoMes = a.getMonth() === b.getMonth() && !loc.startsWith('en');
  const de = mesmoMes ? new Intl.DateTimeFormat(loc, { day: '2-digit' }).format(a) : new Intl.DateTimeFormat(loc, { day: '2-digit', month: 'short' }).format(a);
  return juntar(de, completa.format(b));
}

/** F7: calendário com visão de dia, semana e mês. */
export function Calendario({
  visao,
  referencia,
  eventos,
  aoNavegar,
  aoClicarEvento,
  aoClicarHorario,
}: {
  visao: VisaoCalendario;
  referencia: Date;
  eventos: EventoCalendario[];
  aoNavegar: (nova: Date) => void;
  aoClicarEvento?: (e: EventoCalendario) => void;
  aoClicarHorario?: (inicio: Date) => void;
}) {
  const { t, i18n } = useTranslation();
  const loc = i18n.language === 'pt' ? 'pt-BR' : i18n.language;
  const { de } = intervaloDe(visao, referencia);
  const dias = useMemo(() => Array.from({ length: visao === 'dia' ? 1 : visao === 'semana' ? 7 : 42 }, (_, i) => somarDias(de, i)), [visao, de.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps
  const hoje = new Date();
  const passo = (n: number) =>
    aoNavegar(visao === 'mes' ? new Date(referencia.getFullYear(), referencia.getMonth() + n, 1) : somarDias(referencia, n * (visao === 'semana' ? 7 : 1)));
  const titulo =
    visao === 'mes'
      ? new Intl.DateTimeFormat(loc, { month: 'long', year: 'numeric' }).format(referencia)
      : visao === 'dia'
        ? new Intl.DateTimeFormat(loc, { weekday: 'long', day: '2-digit', month: 'long' }).format(referencia)
        : tituloSemana(loc, dias[0]!, dias[6]!, (de, ate) => t('comum.intervalo', { de, ate }));
  // Eventos agrupados por dia e por hora uma vez só (antes cada célula filtrava a lista inteira: 42 x 6.000 comparações por
  // render, e cada célula de hora da Semana filtrava o dia inteiro).
  const { porDia, porHora } = useMemo(() => {
    const m = new Map<string, EventoCalendario[]>();
    const h = new Map<string, EventoCalendario[]>();
    const incluir = <K,>(mapa: Map<K, EventoCalendario[]>, k: K, e: EventoCalendario) => {
      const l = mapa.get(k);
      if (l) l.push(e);
      else mapa.set(k, [e]);
    };
    for (const e of eventos) {
      const ini = new Date(e.inicio);
      if (e.diaInteiro) {
        // Um por dia ocupado; a linha é a primeira da faixa (nunca abaixo das horas dos outros eventos).
        for (let d = inicioDoDia(ini); d.getTime() < new Date(e.fim).getTime(); d = somarDias(d, 1)) {
          incluir(m, chaveDia(d), e);
          incluir(h, `${chaveDia(d)}:dia`, e);
        }
        continue;
      }
      const k = chaveDia(ini);
      incluir(m, k, e);
      incluir(h, `${k}:${ini.getHours()}`, e);
    }
    for (const l of m.values()) l.sort((a, b) => a.inicio.localeCompare(b.inicio));
    for (const l of h.values()) l.sort((a, b) => a.inicio.localeCompare(b.inicio));
    return { porDia: m, porHora: h };
  }, [eventos]);
  const doDia = (d: Date) => porDia.get(chaveDia(d)) ?? [];
  const faixa = faixaDeHoras(visao === 'mes' ? [] : dias.flatMap((d) => doDia(d).filter((e) => !e.diaInteiro).map((e) => new Date(e.inicio).getHours())));
  const daHora = (d: Date, hora: number) => {
    const daquela = porHora.get(`${chaveDia(d)}:${hora}`) ?? [];
    const inteiros = hora === faixa.inicio ? porHora.get(`${chaveDia(d)}:dia`) : undefined;
    return inteiros ? [...inteiros, ...daquela] : daquela;
  };
  const faixaDe = (e: EventoCalendario) => (e.diaInteiro ? t('calendario.diaInteiro') : t('comum.horario', { de: hora(e.inicio), ate: hora(e.fim) }));
  const [mostrarNaLista, setMostrarNaLista] = useState(LISTA_POR_VEZ);
  // r7 visual M1 e r8 visual B7: dia com eventos (em qualquer largura) e o "+N" abrem a lista do dia, com cada evento e o
  // botão Novo; antes tocar no dia abria "Novo agendamento" e os eventos ficavam inalcançáveis. r11: o "+N" de uma célula de
  // hora (Semana e Dia) abre a lista só daquele horário.
  const [aberta, setAberta] = useState<{ dia: Date; hora?: number } | null>(null);
  const diaAberto = aberta?.dia ?? null;
  const abrirDia = (d: Date, hora?: number) => {
    setMostrarNaLista(LISTA_POR_VEZ);
    setAberta({ dia: d, hora });
  };
  const fecharLista = () => setAberta(null);
  const eventosDoDiaAberto = aberta ? (aberta.hora === undefined ? doDia(aberta.dia) : daHora(aberta.dia, aberta.hora)) : [];
  // r11 visual B3: depois de "Mostrar mais", o foco vai ao 1º item novo (antes caía num item do meio ou, no último lote, sumia
  // com o botão).
  const listaRef = useRef<HTMLUListElement>(null);
  const focarItem = useRef<number | null>(null);
  useEffect(() => {
    const i = focarItem.current;
    if (i === null) return;
    focarItem.current = null;
    listaRef.current?.children[i]?.querySelector<HTMLElement>('[data-item-lista]')?.focus();
  }, [mostrarNaLista]);
  const formatoHora = new Intl.DateTimeFormat(loc, { hour: '2-digit', minute: '2-digit' });
  const dataLongaDe = (d: Date) => new Intl.DateTimeFormat(loc, { weekday: 'long', day: 'numeric', month: 'long' }).format(d);

  return (
    <div className="min-w-0 rounded-xl border border-borda bg-sup-1">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-borda p-3">
        <div className="flex items-center gap-1">
          <Botao variante="fantasma" tamanho="sm" aria-label={t('comum.anterior')} onClick={() => passo(-1)} icone={<ChevronLeft className="size-4" />} />
          <Botao variante="secundario" tamanho="sm" onClick={() => aoNavegar(new Date())}>
            {t('comum.hoje')}
          </Botao>
          <Botao variante="fantasma" tamanho="sm" aria-label={t('comum.proximo')} onClick={() => passo(1)} icone={<ChevronRight className="size-4" />} />
        </div>
        <p className="text-sm font-semibold first-letter:uppercase">{titulo}</p>
      </div>

      {visao === 'mes' ? (
        <div className="grid grid-cols-7">
          {dias.slice(0, 7).map((d) => (
            <div key={`h${d.getDay()}`} className="kicker border-b border-borda px-2 py-1.5 text-center">
              {new Intl.DateTimeFormat(loc, { weekday: 'short' }).format(d)}
            </div>
          ))}
          {dias.map((d) => {
            const evs = doDia(d);
            const foraDoMes = d.getMonth() !== referencia.getMonth();
            const novoNoDia = () => aoClicarHorario?.(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9));
            const dataLonga = dataLongaDe(d);
            // r6 visual B4: o dia é um botão (número do dia) e cada evento é um botão próprio, irmãos na célula; antes o evento era
            // um span dentro do botão do dia e não recebia o Tab. O clique no vazio da célula continua criando no dia.
            // r8 visual B7: o dia que anuncia "N eventos" abre a lista do dia (com Novo dentro) em qualquer largura, como no
            // celular; dia sem eventos cria no dia.
            return (
              <div
                key={d.toISOString()}
                data-dia-calendario
                onClick={(ev) => ev.target === ev.currentTarget && novoNoDia()}
                className={`flex min-h-20 min-w-0 flex-col gap-1 border-r border-b border-borda p-1.5 text-left hover:bg-sup-2 sm:min-h-24 print:min-h-0 print:break-inside-avoid ${foraDoMes ? 'bg-sup-2/50' : ''}`}
              >
                <button
                  type="button"
                  aria-label={evs.length ? `${dataLonga}, ${t('calendario.eventos', { count: evs.length })}` : dataLonga}
                  aria-haspopup={evs.length ? 'dialog' : undefined}
                  onClick={() => (evs.length ? abrirDia(d) : novoNoDia())}
                  className={`numero min-h-6 w-full rounded text-left text-xs ${mesmoDia(d, hoje) ? 'font-bold text-acento-forte' : foraDoMes ? 'text-tinta-3' : 'text-tinta-1'}`}
                >
                  {d.getDate()}
                  {evs.length > 0 && <span className="ml-1.5 text-tinta-3 sm:hidden">{evs.length}</span>}
                </button>
                {evs.slice(0, IMPRESSAO_POR_DIA).map((e, i) => {
                  const texto = e.diaInteiro ? e.titulo : `${hora(e.inicio)} ${e.titulo}`;
                  // r8 visual B6: no desktop o evento tem altura mínima de 24px (alvo de toque e leitura). Na impressão saem
                  // até IMPRESSAO_POR_DIA eventos do dia, com "+N" para o resto (na tela, os 3 primeiros e o "+N"; r10 robustez B1).
                  const visual = visualDoEvento(e, !e.estatico);
                  const classe = `w-full min-h-6 min-w-0 truncate rounded px-1 py-0.5 text-left text-xs leading-5 ${visual.classe} ${i < TELA_POR_DIA ? 'hidden sm:block' : 'hidden print:block'}`;
                  return e.estatico ? (
                    <TextoCortado key={e.id} como="p" data-bloqueio-calendario style={visual.style} className={`${classe} text-tinta-3`} texto={e.subtitulo ? `${texto}, ${e.subtitulo}` : texto}>
                      {texto}
                    </TextoCortado>
                  ) : (
                    <button
                      type="button"
                      key={e.id}
                      data-evento-calendario
                      title={e.subtitulo ? `${texto}, ${e.subtitulo}` : texto}
                      onClick={() => aoClicarEvento?.(e)}
                      style={visual.style}
                      className={classe}
                    >
                      {texto}
                    </button>
                  );
                })}
                {evs.length > TELA_POR_DIA && (
                  <button
                    type="button"
                    data-sem-impressao
                    aria-haspopup="dialog"
                    aria-label={`${dataLonga}, ${t('calendario.eventos', { count: evs.length })}`}
                    onClick={() => abrirDia(d)}
                    className="hidden min-h-6 w-full rounded px-1 text-left text-xs text-tinta-3 hover:bg-sup-3 hover:text-tinta-1 sm:block"
                  >
                    +{evs.length - TELA_POR_DIA}
                  </button>
                )}
                {evs.length > IMPRESSAO_POR_DIA && <p className="hidden px-1 text-xs text-tinta-3 print:block">+{evs.length - IMPRESSAO_POR_DIA}</p>}
              </div>
            );
          })}
        </div>
      ) : (
        // Na impressão a grade cabe na folha (sem largura mínima nem rolagem): o domingo não fica de fora (r8 visual M2).
        <div className="overflow-x-auto print:overflow-visible">
          <div className={`grid ${visao === 'dia' ? 'grid-cols-6' : 'min-w-3xl grid-cols-8 print:min-w-0'}`}>
            <div className="border-b border-borda" />
            {dias.map((d) => (
              <div key={d.toISOString()} className={`border-b border-l border-borda px-2 py-1.5 ${visao === 'dia' ? 'col-span-5' : ''} text-center text-xs ${mesmoDia(d, hoje) ? 'text-acento-forte' : 'text-tinta-3'}`}>
                <span className="kicker">{new Intl.DateTimeFormat(loc, { weekday: 'short' }).format(d)}</span> <span className="numero font-semibold">{d.getDate()}</span>
              </div>
            ))}
            {Array.from({ length: faixa.fim - faixa.inicio }, (_, i) => faixa.inicio + i).map((h) => (
              <div key={h} className="contents">
                <div className="numero border-b border-borda pt-1 pr-1 text-right text-xs text-tinta-3">{String(h).padStart(2, '0')}h</div>
                {dias.map((d) => {
                  const evs = daHora(d, h);
                  const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h);
                  const quando = `${dataLongaDe(d)}, ${formatoHora.format(inicio)}`;
                  // r8 visual M5: a célula não é mais um role="button" sem nome com botões dentro (nested-interactive). Os eventos
                  // vêm primeiro e o botão de criar, com nome (dia e hora no idioma), é irmão deles e ocupa o resto da célula
                  // (no mínimo 24px de alvo, sem nada por cima).
                  return (
                    <div key={d.toISOString() + h} className={`flex min-h-12 min-w-0 flex-col gap-1 border-b border-l border-borda p-1 print:min-h-0 ${visao === 'dia' ? 'col-span-5' : ''}`}>
                      {evs.slice(0, IMPRESSAO_POR_DIA).map((e, i) =>
                        e.estatico ? (
                          <div key={e.id} data-bloqueio-calendario style={visualDoEvento(e, false).style} className={`min-w-0 rounded px-1.5 py-1 text-left text-xs text-tinta-3 ${visualDoEvento(e, false).classe} ${i < TELA_POR_DIA ? '' : 'hidden print:block'}`}>
                            <span className="numero block">{faixaDe(e)}</span>
                            <TextoCortado className="block font-medium">{e.titulo}</TextoCortado>
                          </div>
                        ) : (
                          <button
                            type="button"
                            key={e.id}
                            data-evento-calendario
                            onClick={() => aoClicarEvento?.(e)}
                            style={visualDoEvento(e).style}
                            className={`min-w-0 rounded px-1.5 py-1 text-left text-xs ${visualDoEvento(e).classe} ${i < TELA_POR_DIA ? '' : 'hidden print:block'}`}
                          >
                            <span className="numero block text-tinta-3">{faixaDe(e)}</span>
                            <TextoCortado className="block font-medium">{e.titulo}</TextoCortado>
                            {e.subtitulo && <TextoCortado className="block text-tinta-3">{e.subtitulo}</TextoCortado>}
                          </button>
                        ),
                      )}
                      {evs.length > TELA_POR_DIA && (
                        <button
                          type="button"
                          data-mais-horario
                          data-sem-impressao
                          aria-haspopup="dialog"
                          aria-label={`${quando}, ${t('calendario.eventos', { count: evs.length })}`}
                          onClick={() => abrirDia(d, h)}
                          className="min-h-6 w-full rounded px-1 text-left text-xs text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
                        >
                          +{evs.length - TELA_POR_DIA}
                        </button>
                      )}
                      {evs.length > IMPRESSAO_POR_DIA && <p className="hidden px-1 text-xs text-tinta-3 print:block">+{evs.length - IMPRESSAO_POR_DIA}</p>}
                      {aoClicarHorario && (
                        <button
                          type="button"
                          data-horario
                          data-sem-impressao
                          aria-label={t('calendario.criarEm', { quando })}
                          onClick={() => aoClicarHorario(inicio)}
                          className="min-h-6 w-full flex-1 rounded hover:bg-sup-2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal
        aberto={!!aberta}
        aoFechar={fecharLista}
        titulo={
          aberta
            ? aberta.hora === undefined
              ? t('calendario.eventosDoDia', { data: dataLongaDe(aberta.dia) })
              : t('calendario.eventosDoHorario', { data: dataLongaDe(aberta.dia), hora: formatoHora.format(new Date(aberta.dia.getFullYear(), aberta.dia.getMonth(), aberta.dia.getDate(), aberta.hora)) })
            : ''
        }
        rodape={
          aoClicarHorario && (
            <Botao
              variante="primario"
              icone={<Plus className="size-4" />}
              onClick={() => {
                const d = diaAberto!;
                const hora = aberta?.hora ?? 9;
                fecharLista();
                aoClicarHorario(new Date(d.getFullYear(), d.getMonth(), d.getDate(), hora));
              }}
            >
              {t('comum.novo')}
            </Botao>
          )
        }
      >
        <ul ref={listaRef} className="flex flex-col gap-2" data-eventos-do-dia>
          {eventosDoDiaAberto.slice(0, mostrarNaLista).map((e) => (
            <li key={e.id}>
              {e.estatico ? (
                <p data-bloqueio-calendario data-item-lista tabIndex={-1} style={visualDoEvento(e, false).style} className={`flex w-full min-w-0 flex-col rounded px-3 py-2 text-sm text-tinta-2 focus:outline-2 focus:outline-foco ${visualDoEvento(e, false).classe}`}>
                  <span className="numero text-xs text-tinta-3">{faixaDe(e)}</span>
                  <span className="font-medium break-words">{e.titulo}</span>
                  {e.subtitulo && <span className="text-xs break-words text-tinta-3">{e.subtitulo}</span>}
                </p>
              ) : (
              <button
                type="button"
                data-evento-calendario
                data-item-lista
                onClick={() => {
                  fecharLista();
                  aoClicarEvento?.(e);
                }}
                style={visualDoEvento(e).style}
                className={`flex w-full min-w-0 flex-col rounded px-3 py-2 text-left text-sm ${visualDoEvento(e).classe}`}
              >
                <span className="numero text-xs text-tinta-3">{faixaDe(e)}</span>
                <span className="font-medium break-words">{e.titulo}</span>
                {e.subtitulo && <span className="text-xs break-words text-tinta-3">{e.subtitulo}</span>}
              </button>
              )}
            </li>
          ))}
        </ul>
        {eventosDoDiaAberto.length > mostrarNaLista && (
          <Botao variante="secundario" tamanho="sm" className="mt-3" data-mostrar-mais-dia onClick={() => {
              focarItem.current = mostrarNaLista;
              setMostrarNaLista((n) => n + LISTA_POR_VEZ);
            }}>
            {t('calendario.mostrarMais', { n: Math.min(LISTA_POR_VEZ, eventosDoDiaAberto.length - mostrarNaLista), total: eventosDoDiaAberto.length - mostrarNaLista })}
          </Botao>
        )}
      </Modal>
    </div>
  );
}
